import { useBillingRepository } from '@Hooks/useRepository';
import { type MarkAsPaidOptions } from '@Model';
import { InvoicesRepository } from '@Repositories/Billing';
import { isOk, useToasts } from '@synqo/frontend-component-library';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';

export function useMarkInvoicesAsPaid(invoiceIds: string[]) {
    const invoices = useBillingRepository(InvoicesRepository);
    const { pushToast, remove, pushAllToasts } = useToasts();

    const { mutateAsync: markAsPaidMutation, isPending } = useMutation({
        mutationFn: (options: MarkAsPaidOptions) => invoices.markAsPaid(options),
    });

    const markAsPaid = useCallback(
        async (paymentDate: string) => {
            const messageId = pushToast({
                title: 'Bezahlung läuft',
                message: `${invoiceIds.length} Rechnung(en) werden als bezahlt markiert`,
                timeout: undefined,
                loading: true,
                variant: 'success',
            });

            const options: MarkAsPaidOptions = {
                invoice_ids: invoiceIds,
                payment_date: paymentDate,
            };

            const markAsPaidResult = await markAsPaidMutation(options).finally(() => remove(messageId));

            if (isOk(markAsPaidResult)) {
                const numberSuccessful = markAsPaidResult.data;
                const message =
                    numberSuccessful === 1
                        ? 'Rechnung als bezahlt markiert.'
                        : `${numberSuccessful} Rechnungen als bezahlt markiert.`;
                pushToast({
                    title: 'Rechnungen bezahlt',
                    message: message,
                    timeout: 5_000,
                    variant: 'success',
                });
            } else {
                pushAllToasts(markAsPaidResult.messages);
            }
        },
        [invoiceIds, pushToast, remove, markAsPaidMutation, pushAllToasts],
    );

    return {
        markAsPaid,
        isPending,
    };
}
